import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, Spinner, InfoRow, Input, Textarea, RichSelect,
} from './shared';

const WINDOW_TYPES = [
    { value: 'weekly', label: 'Weekly', desc: 'Giới hạn theo tuần, reset mỗi thứ 2' },
    { value: 'monthly', label: 'Monthly', desc: 'Giới hạn theo tháng, reset đầu tháng' },
    { value: 'daily', label: 'Daily', desc: 'Giới hạn theo ngày, reset mỗi 00:00' },
    { value: 'trial', label: 'Trial', desc: 'Dùng thử một lần, hết hạn là dừng' },
];
const RESET_MODES = [
    { value: 'calendar', label: 'Calendar', desc: 'Reset theo lịch cố định (thứ 2, đầu tháng, 00:00)' },
    { value: 'subscription', label: 'Subscription', desc: 'Reset từ ngày đăng ký subscription + N ngày' },
    { value: 'rolling', label: 'Rolling', desc: 'Reset sau N ngày tính từ lần dùng đầu tiên' },
    { value: 'one_time', label: 'One-time', desc: 'Dùng 1 lần, không reset, hết thì hết' },
];
const TIER_BADGES = { free: 'default', pro: 'info', premium: 'purple' };

const fmtLimit = (v) => v == null ? '—' : v.toLocaleString();

const INITIAL_FORM = {
    tier_name: '', window_type: '', reset_mode: 'calendar',
    cost_limit: '', request_limit: '', token_limit: '',
    window_duration_days: '', is_active: true, description: '',
};

export default function AdminTierWindowLimits() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterTier, setFilterTier] = useState('');
    const [selected, setSelected] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => { loadItems(); }, [filterTier]);

    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await apiClient.listTierWindowLimits(filterTier || undefined);
            setItems(res.items || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelected({ ...INITIAL_FORM });
        setIsCreateMode(true);
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelected({
            ...item,
            cost_limit: item.cost_limit ?? '',
            request_limit: item.request_limit ?? '',
            token_limit: item.token_limit ?? '',
            window_duration_days: item.window_duration_days ?? '',
            description: item.description || '',
        });
        setIsCreateMode(false);
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = {
                ...selected,
                cost_limit: selected.cost_limit === '' ? null : parseFloat(selected.cost_limit),
                request_limit: selected.request_limit === '' ? null : parseInt(selected.request_limit),
                token_limit: selected.token_limit === '' ? null : parseInt(selected.token_limit),
                window_duration_days: selected.window_duration_days === '' ? null : parseInt(selected.window_duration_days),
            };

            if (isCreateMode) {
                await apiClient.createTierWindowLimit(payload);
                setSuccessMessage('Created successfully');
            } else {
                await apiClient.updateTierWindowLimit(selected.id, payload);
                setSuccessMessage('Updated successfully');
            }
            setIsModalOpen(false);
            loadItems();
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this limit?')) return;
        try {
            await apiClient.deleteTierWindowLimit(id);
            setSuccessMessage('Deleted');
            loadItems();
        } catch (err) {
            setError(err.message || 'Delete failed');
        }
    };

    const tiers = [...new Set(items.map(i => i.tier_name))].sort();

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Tier Window Limits</h2>
                <Button onClick={handleCreate}>+ Add Limit</Button>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert type="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">Filter:</span>
                <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                >
                    <option value="">All tiers</option>
                    {tiers.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {loading ? <Spinner /> : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-left text-gray-500">
                                    <th className="px-4 py-2">Tier</th>
                                    <th className="px-4 py-2">Window</th>
                                    <th className="px-4 py-2">Reset</th>
                                    <th className="px-4 py-2">Cost</th>
                                    <th className="px-4 py-2">Requests</th>
                                    <th className="px-4 py-2">Tokens</th>
                                    <th className="px-4 py-2">Days</th>
                                    <th className="px-4 py-2">Active</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => handleEdit(item)}>
                                        <td className="px-4 py-2">
                                            <Badge variant={TIER_BADGES[item.tier_name] || 'default'}>{item.tier_name}</Badge>
                                        </td>
                                        <td className="px-4 py-2 font-medium">{item.window_type}</td>
                                        <td className="px-4 py-2 text-gray-500">{item.reset_mode}</td>
                                        <td className="px-4 py-2">{item.cost_limit != null ? `$${item.cost_limit}` : '—'}</td>
                                        <td className="px-4 py-2">{fmtLimit(item.request_limit)}</td>
                                        <td className="px-4 py-2">{fmtLimit(item.token_limit)}</td>
                                        <td className="px-4 py-2">{item.window_duration_days ?? '—'}</td>
                                        <td className="px-4 py-2">
                                            <span className={`w-2 h-2 rounded-full inline-block ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                        </td>
                                        <td className="px-4 py-2">
                                            <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No limits configured</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} title={isCreateMode ? 'Create Tier Window Limit' : 'Edit Tier Window Limit'} size="md">
                {selected && (
                    <div className="p-6 space-y-4">
                        {isCreateMode && (
                            <>
                                <Input label="Tier Name" value={selected.tier_name} onChange={(e) => setSelected({ ...selected, tier_name: e.target.value })} placeholder="pro, free, premium" />
                                <RichSelect
                                    label="Window Type"
                                    value={selected.window_type}
                                    onChange={(v) => setSelected({ ...selected, window_type: v })}
                                    options={WINDOW_TYPES}
                                    disabled={!isCreateMode}
                                />
                            </>
                        )}
                        <RichSelect
                            label="Reset Mode"
                            value={selected.reset_mode}
                            onChange={(v) => setSelected({ ...selected, reset_mode: v })}
                            options={RESET_MODES}
                        />
                        <Input label="Cost Limit ($)" type="number" step="0.01" value={selected.cost_limit} onChange={(e) => setSelected({ ...selected, cost_limit: e.target.value })} placeholder="NULL = unlimited" />
                        <Input label="Request Limit" type="number" value={selected.request_limit} onChange={(e) => setSelected({ ...selected, request_limit: e.target.value })} placeholder="NULL = unlimited" />
                        <Input label="Token Limit" type="number" value={selected.token_limit} onChange={(e) => setSelected({ ...selected, token_limit: e.target.value })} placeholder="NULL = unlimited" />
                        <Input label="Duration (days)" type="number" value={selected.window_duration_days} onChange={(e) => setSelected({ ...selected, window_duration_days: e.target.value })} placeholder="For trial/subscription" />
                        <div className="flex items-center gap-2">
                            <input type="checkbox" checked={selected.is_active} onChange={(e) => setSelected({ ...selected, is_active: e.target.checked })} className="rounded" />
                            <label className="text-sm text-gray-700">Active</label>
                        </div>
                        <Textarea label="Description" value={selected.description} onChange={(e) => setSelected({ ...selected, description: e.target.value })} rows={2} />
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
